import { useState, useMemo, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import gradientChartLine from "@/theme/functions/gradientChartLine";
import colors from "@/theme/base/colors";
import Box from "@/elements/Box";
import configs from "./configs";
import { ChartDataset, ScriptableLineSegmentContext } from "chart.js";
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  CategoryScale,
  Filler,
  Tooltip,
  Colors,
} from "chart.js";
import "chartjs-adapter-moment";
import moment from "moment";
import {
  NexusOverlayDto,
  ProprietaMisurataDto,
  ProprietaMisurataValueDto,
} from "@/api";

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  CategoryScale,
  Filler,
  Tooltip,
  Colors
);

export type ChartData = {
  proprietaMisurata: ProprietaMisurataDto;
  values: ProprietaMisurataValueDto[];
};

const unknownRpm = 1;

function ProprietaMisurataHistoryChart({
  data: _data,
  nexusOverlay,
  height,
}: {
  data: ChartData[];
  nexusOverlay?: NexusOverlayDto[] | null;
  height?: number | null;
}) {
  const { t } = useTranslation();
  const [domChart, setDomChart] = useState<any>(null);
  const chartData = useMemo(() => {
    const datasets: ChartDataset<"line", { x: number; y: number | null }[]>[] =
      [];
    const proprietaMisurate: ProprietaMisurataDto[] = [];

    if (domChart) {
      for (const d of _data) {
        const helper = d.proprietaMisurata.type;
        if (helper) {
          proprietaMisurate.push(d.proprietaMisurata);

          const dataset: ChartDataset<
            "line",
            { x: number; y: number | null }[]
          > = {
            label: helper.label,
            tension: 0,
            pointRadius: 0.25,
            borderWidth: 3,
            data: d.values.map((v) => {
              return {
                x: moment(v.timestamp).valueOf(),
                y: v.value ?? null,
              };
            }),
            yAxisID: helper.unit ?? "scalar",

            borderColor: helper.colorHex ?? undefined,
            // backgroundColor: helper.colorHex
            //   ? `${helper.colorHex}33`
            //   : undefined,
            pointBackgroundColor: helper.colorHex ?? undefined,
            pointBorderColor: helper.colorHex ?? undefined,
            segment: {
              borderColor: helper.colorHex ?? undefined,
            },
            fill: false,
          };
          datasets.push(dataset);
        }
      }

      const enrichWithMinMaxError = datasets.length === 1;
      if (enrichWithMinMaxError) {
        const dataset = datasets[0];
        const values = _data[0].values;

        const getGradientChartLine = (color: any) => {
          return gradientChartLine(
            domChart.children[0],
            colors[color as "dark"]
              ? colors[(color as "dark") || "dark"].main
              : colors.dark.main
          );
        };
        const getColor = (color: any) => {
          return colors[color as "dark"]
            ? colors[(color as "dark") || "dark"].main
            : colors.dark.main;
        };

        const normalColor = dataset.borderColor ?? getColor("info");
        const errorColor = getColor("error");
        const normalBg = dataset.borderColor
          ? `${dataset.borderColor}33`
          : getGradientChartLine("info");
        const errorBg = getGradientChartLine("error");

        const hasError = (index: number, value: number | null) => {
          const min = values[index].dangerMin;
          const max = values[index].dangerMax;

          const err =
            typeof value === "number"
              ? typeof min === "number" && value <= min
                ? true
                : typeof max === "number" && value >= max
                ? true
                : false
              : false;

          return err;
        };
        const hasErrorSegment = (context: ScriptableLineSegmentContext) => {
          const val0 = context.p0.parsed.y;
          const val1 = context.p1.parsed.y;
          const index0 = context.p0DataIndex;
          const index1 = context.p1DataIndex;
          return hasError(index0, val0) || hasError(index1, val1);
        };

        const getColorWithError = () => {
          return function (context: ScriptableLineSegmentContext) {
            const err = hasErrorSegment(context);
            const c = err ? errorColor : normalColor;
            return c;
          };
        };

        const getBackgroundColorWithError = () => {
          return function (context: ScriptableLineSegmentContext) {
            const err = hasErrorSegment(context);
            const bg = err ? errorBg : normalBg;
            return bg;
          };
        };

        dataset.segment = {
          borderColor: getColorWithError() as any,
          backgroundColor: getBackgroundColorWithError(),
        };
        dataset.backgroundColor = normalBg;
        dataset.fill = "start";

        datasets.push({
          label: t("proprietaMisurataHistoryChart.legenda.max"),
          data: values.map((v) => {
            return {
              x: moment(v.timestamp).valueOf(),
              y: v.dangerMax ?? null,
            };
          }),
          borderWidth: 1,
          borderColor: getColor("error"),
          stepped: "middle" as "middle",
          backgroundColor: "transparent",
          tension: 0,
          pointRadius: 0,
          yAxisID: dataset.yAxisID,
        });

        datasets.push({
          label: t("proprietaMisurataHistoryChart.legenda.min"),
          data: values.map((v) => {
            return {
              x: moment(v.timestamp).valueOf(),
              y: v.dangerMin ?? null,
            };
          }),
          borderWidth: 1,
          borderColor: getColor("error"),
          stepped: "middle" as "middle",
          backgroundColor: "transparent",
          tension: 0,
          pointRadius: 0,
          yAxisID: dataset.yAxisID,
        });
      }
    }

    if (nexusOverlay && nexusOverlay.length > 0) {
      const maxRpm =
        nexusOverlay.reduce((acc, point) => {
          if (typeof point.rpm === "number" && Number.isFinite(point.rpm)) {
            return Math.max(acc, point.rpm);
          }
          return acc;
        }, 0) || unknownRpm;

      datasets.push({
        label: t("proprietaMisurataHistoryChart.legenda.analisiNexus"),
        // tension: 0,
        // pointRadius: 0,
        borderWidth: 0,
        stepped: true,
        data: nexusOverlay.map((point) => {
          const y = point.analisiAttiva
            ? typeof point.rpm === "number"
              ? point.rpm
              : unknownRpm
            : 0;

          return {
            x: moment(point.timestamp).valueOf(),
            y,
          };
        }),
        yAxisID: "rpmNexus",
        borderColor: "rgba(255, 152, 0, 0.18)",
        backgroundColor: "rgba(255, 152, 0, 0.18)",
        fill: { value: 0 },
        // order: -10,
      });

      return configs(datasets, proprietaMisurate, maxRpm);
    }

    return configs(datasets, proprietaMisurate, null);
  }, [_data, domChart, nexusOverlay, t]);

  // lastValuesRef.current = values;

  return (
    <Box
      ref={useCallback((dom: any) => setDomChart(dom), [])}
      sx={{ height: height ?? 300 }}
      flex={1}
    >
      <Line data={chartData.data} options={chartData.options} />
    </Box>
  );
}

export default ProprietaMisurataHistoryChart;

function getAnimation(
  values: ProprietaMisurataValueDto[],
  last: ProprietaMisurataValueDto[] | null
): false | undefined {
  if (!last) {
    return undefined;
  }
  // è solo un cambiamento di zoom se:
  // - il primo elemento è ancora lo stesso (lo zoom è fatto a destra)
  // - il primo elemento c'era anche prima (lo zoom è fatto a sinistra -> muovendo verso destra)
  // - il primo elemento di prima c'è ancora (lo zoom è fatto a sinistra -> muovendo verso sinistra)
  const firstPre = last[0];
  const firstNext = values[0];
  if (firstPre && firstNext) {
    return firstPre === firstNext ||
      last.includes(firstNext) ||
      values.includes(firstPre)
      ? false
      : undefined;
  }
  return undefined;
}
